import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { api, type User, type Challenge } from "../lib/api";

interface ActiveChallenge extends Challenge {
  userSide: string | null;
  userVote: string | null;
  opponentUsername: string | null;
  opponentPhotoUrl: string | null;
  needsAction: boolean;
}

interface Stats {
  wins: number;
  losses: number;
  draws: number;
  totalEarned: number;
}

interface AppDataContextValue {
  // User data
  user: User | null;
  demoMode: boolean;

  // Challenges data
  activeChallenges: ActiveChallenge[];
  allChallenges: Challenge[];
  balance: number;
  stats: Stats;

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Actions
  refresh: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateBalance: (newBalance: number) => void;
  setUser: (user: User | null) => void;
}

const AppDataContext = createContext<AppDataContextValue | null>(null);

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

export function AppDataProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [demoMode, setDemoMode] = useState(false);
  const [activeChallenges, setActiveChallenges] = useState<ActiveChallenge[]>([]);
  const [allChallenges, setAllChallenges] = useState<Challenge[]>([]);
  const [balance, setBalance] = useState(0);
  const [stats, setStats] = useState<Stats>({ wins: 0, losses: 0, draws: 0, totalEarned: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await api.getMe();
      setUser(userData.user);
      setDemoMode(userData.demoMode);
      setBalance(userData.user.balance);
    } catch (err) {
      console.error("Failed to fetch user:", err);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      setError(null);

      // Check if we have Telegram auth
      const webApp = window.Telegram?.WebApp;
      const hasAuth = webApp?.initDataUnsafe?.user;
      console.log("[AppData] Refreshing, hasAuth:", !!hasAuth);

      // Fetch all data in parallel
      const [userData, activeData, allData] = await Promise.all([
        api.getMe(),
        api.getActiveChallenges(),
        api.getMyChallenges(),
      ]);

      setUser(userData.user);
      setDemoMode(userData.demoMode);
      setActiveChallenges(activeData.challenges);
      setStats({
        wins: activeData.stats.wins,
        losses: activeData.stats.losses,
        draws: activeData.stats.draws ?? 0,
        totalEarned: activeData.stats.totalEarned,
      });
      setAllChallenges(allData.challenges);
      setBalance(allData.balance);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      // Check if it's an auth error
      const webApp = window.Telegram?.WebApp;
      if (webApp && !webApp.initDataUnsafe?.user) {
        setError("Auth error: Please open this app from Telegram");
      } else {
        setError("Failed to load data. Check console for details.");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateBalance = useCallback((newBalance: number) => {
    setBalance(newBalance);
    setUser(prev => prev ? { ...prev, balance: newBalance } : null);
  }, []);

  // Initial fetch
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-refresh in background
  useEffect(() => {
    const intervalId = setInterval(() => {
      // Only refresh if not in initial loading state
      if (!isLoading) {
        refresh();
      }
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [refresh, isLoading]);

  const value: AppDataContextValue = {
    user,
    demoMode,
    activeChallenges,
    allChallenges,
    balance,
    stats,
    isLoading,
    error,
    refresh,
    refreshUser,
    updateBalance,
    setUser,
  };

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error("useAppData must be used within an AppDataProvider");
  }
  return context;
}

export type { ActiveChallenge, Stats };
